import { useEffect, useMemo, useState } from "react";
import { Book, Search } from "lucide-react";
import { API, axios, formatINR, kbCategoryTone } from "../lib/shared";

// KnowledgeBase was previously colocated in App.js — moved here in the 2026-03 cleanup.

function Badge({ children, tone = "blue", "data-testid": tid }) {
  return <span data-testid={tid || `badge-${String(children).toLowerCase().replaceAll(" ", "-")}`} className={`badge badge-${tone}`}>{children}</span>;
}

function PageHeader({ eyebrow, title, subtitle, action }) {
  return <div className="page-header">
    <div><div className="eyebrow">{eyebrow}</div><h1 data-testid="page-header-title">{title}</h1><p>{subtitle}</p></div>
    {action}
  </div>;
}

export default function KnowledgeBase() {
  const [data, setData] = useState({ total: 0, counts: {}, items: [] });
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/knowledge-base`, { params: { category, search } })
      .then(r => {
        setData(r.data);
        setActive(prev => prev ? (r.data.items.find(x => x.slug === prev.slug) || r.data.items[0] || null) : r.data.items[0] || null);
      })
      .catch(console.error);
  }, [category, search, reloadKey]);

  const tabs = [
    { id: "all", label: "All", count: data.total_all || 0 },
    { id: "plan", label: "Plans", count: data.counts.plan || 0 },
    { id: "policy", label: "Policies", count: data.counts.policy || 0 },
    { id: "faq", label: "FAQs", count: data.counts.faq || 0 },
    { id: "contact", label: "Contact", count: data.counts.contact || 0 },
  ];

  return <>
    <PageHeader
      eyebrow="REFERENCE / SOURCE OF TRUTH"
      title="Knowledge base"
      subtitle="Every plan, price and policy the AI and sales team should trust — grounded in the 2026 brochure and subscription policy."
      action={<div className="health"><i /> 2 documents ingested</div>}
    />
    <div className="kb-toolbar">
      <div className="kb-tabs">{tabs.map(t => <button key={t.id} data-testid={`kb-tab-${t.id}`} className={`kb-tab${category === t.id ? " active" : ""}`} onClick={() => setCategory(t.id)}>{t.label} <em>{t.count}</em></button>)}</div>
      <div className="search-box kb-search"><Search size={15} /><input data-testid="kb-search-input" placeholder="Search knowledge base…" value={search} onChange={e => setSearch(e.target.value)} /></div>
    </div>
    <div className="kb-grid">
      <section className="panel kb-list">
        {data.items.length === 0 ? <div className="empty-state"><Book size={26} /><strong>Nothing here yet</strong><span>Adjust search or category.</span></div> :
          data.items.map(item => (
            <button data-testid={`kb-item-${item.slug}`} key={item.slug} className={`kb-row${active?.slug === item.slug ? " active" : ""}`} onClick={() => setActive(item)}>
              <div className="kb-row-top"><strong>{item.title}</strong><Badge tone={kbCategoryTone(item.category)}>{item.category.toUpperCase()}</Badge></div>
              <p>{item.content.length > 120 ? item.content.slice(0, 117) + "…" : item.content}</p>
            </button>
          ))
        }
      </section>
      {active
        ? <KnowledgeDetail entry={active} onSaved={() => setReloadKey(k => k + 1)} />
        : <aside className="panel kb-detail"><div className="empty-state"><Book size={30} /><strong>Select an entry</strong><span>Choose any plan, policy, or FAQ on the left.</span></div></aside>}
    </div>
  </>;
}

function KnowledgeDetail({ entry, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(entry.title);
  const [content, setContent] = useState(entry.content);
  const [startingAt, setStartingAt] = useState(entry.metadata?.starting_at ?? "");
  const [offers, setOffers] = useState((entry.metadata?.offers || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const entrySlug = entry.slug;
  const entryTitle = entry.title;
  const entryContent = entry.content;
  const entryStartingAt = entry.metadata?.starting_at ?? "";
  const entryOffers = useMemo(() => (entry.metadata?.offers || []).join("\n"), [entry.metadata]);
  useEffect(() => {
    setEditing(false); setTitle(entryTitle); setContent(entryContent);
    setStartingAt(entryStartingAt); setOffers(entryOffers);
  }, [entrySlug, entryTitle, entryContent, entryStartingAt, entryOffers]);
  const save = async () => {
    setSaving(true);
    const body = { title, content };
    if (entry.category === "plan") {
      body.metadata = {
        ...entry.metadata,
        starting_at: startingAt === "" ? null : Number(startingAt),
        offers: offers.split("\n").map(x => x.trim()).filter(Boolean),
      };
    }
    try { await axios.patch(`${API}/knowledge-base/${entry.slug}`, body); setEditing(false); onSaved(); }
    finally { setSaving(false); }
  };
  return <aside className="panel kb-detail">
    <div className="kb-detail-header">
      <div className="kb-detail-header-top">
        <div className="eyebrow">{entry.category.toUpperCase()} · {entry.source.replaceAll("_", " ").toUpperCase()}</div>
        {!editing
          ? <button data-testid="kb-edit-button" className="button button-ghost kb-edit-btn" onClick={() => setEditing(true)}>Edit</button>
          : <div className="kb-edit-actions">
              <button data-testid="kb-save-button" className="button button-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button data-testid="kb-cancel-button" className="button" onClick={() => setEditing(false)}>Cancel</button>
            </div>}
      </div>
      {editing
        ? <input data-testid="kb-edit-title" className="kb-edit-title-input" value={title} onChange={e => setTitle(e.target.value)} />
        : <h2 data-testid="kb-detail-title">{title}</h2>}
    </div>
    {editing
      ? <textarea data-testid="kb-edit-content" className="kb-edit-content" value={content} onChange={e => setContent(e.target.value)} />
      : <p className="kb-detail-body" data-testid="kb-detail-content">{content}</p>}
    {entry.category === "plan" && editing && <div className="kb-meta kb-edit-meta">
      <div className="kb-meta-row"><small>STARTING AT (₹)</small><input data-testid="kb-edit-starting-at" type="number" className="kb-edit-input" value={startingAt} onChange={e => setStartingAt(e.target.value)} placeholder="e.g. 300" /></div>
      <div className="kb-meta-row"><small>OFFERS (one per line)</small><textarea data-testid="kb-edit-offers" className="kb-edit-input kb-edit-offers" rows={3} value={offers} onChange={e => setOffers(e.target.value)} placeholder="5% OFF on 24 Days" /></div>
    </div>}
    {entry.category === "plan" && !editing && entry.metadata && <KnowledgePlanMeta meta={entry.metadata} />}
    {entry.updated_at && <div className="kb-updated">Last updated: {new Date(entry.updated_at).toLocaleString()}</div>}
  </aside>;
}

function KnowledgePlanMeta({ meta }) {
  const prices = meta.prices || {};
  const rows = Object.entries(prices);
  return <div className="kb-meta">
    {meta.meal_type && <div className="kb-meta-row"><small>MEAL TYPE</small><b>{meta.meal_type}</b></div>}
    {meta.starting_at != null && <div className="kb-meta-row"><small>STARTING AT</small><b>{formatINR(meta.starting_at)}</b></div>}
    {meta.targets && Object.keys(meta.targets).length > 0 && (
      <div className="kb-meta-row"><small>NUTRITIONAL TARGET</small>
        <div className="kb-target-list">{Object.entries(meta.targets).map(([k, v]) => <span key={k}><em>{k.replaceAll("_", " ")}</em> · {v}</span>)}</div>
      </div>
    )}
    {rows.length > 0 && <div className="kb-meta-row"><small>PRICING (₹)</small>
      <table className="kb-price-table"><thead><tr><th>Tier</th>{Object.keys(rows[0][1] || {}).map(col => <th key={col}>{col.replaceAll("_", " ")}</th>)}</tr></thead>
        <tbody>{rows.map(([tier, values]) => <tr key={tier}><td>{tier.replaceAll("_", " ")}</td>{typeof values === "object" ? Object.entries(values).map(([col, v]) => <td key={col}>{formatINR(v)}</td>) : <td>{formatINR(values)}</td>}</tr>)}</tbody>
      </table>
    </div>}
    {meta.offers && meta.offers.length > 0 && <div className="kb-meta-row"><small>OFFERS</small><ul className="kb-offer-list">{meta.offers.map(o => <li key={o}>{o}</li>)}</ul></div>}
  </div>;
}
